import { captureMessage } from '@sentry/react';
import type Big from 'big.js';

import { AVERAGE_HOURS_IN_MONTH } from '@cast/constants';
import { getSummaryTendency } from '@cast/features-cluster-shared';
import { Memory } from '@cast/shared-types';
import {
  toBig,
  convertUnit,
  toNumOrZero,
  isMemoryUnit,
  normalizeMemoryUnit,
} from '@cast/utils';

import { safeDivide } from 'utils/safeDivide';

import { calculationsMemoryUnit } from './constants';

export const getCostPerMonth = (
  amount?: number | string | Big,
  hourlyCostPerUnit?: number | string | Big
): number =>
  Math.max(
    toBig(amount)
      .times(toBig(hourlyCostPerUnit))
      .times(AVERAGE_HOURS_IN_MONTH)
      .toNumber(),
    0
  );

const getOverprovisionedPercent = (
  requested?: number | string | Big,
  provisioned?: number | string | Big
): number => {
  return toBig(
    safeDivide(toBig(provisioned).minus(toBig(requested)), toBig(provisioned))
  )
    .times(100)
    .toNumber();
};

export const getEfficiencyPercent = (
  requested?: number | string | Big,
  provisioned?: number | string | Big
): number => {
  if (!requested || !provisioned) {
    return 0;
  }

  // if there are no provisioned resources, we assume data is still loading
  if (toNumOrZero(provisioned) === 0) {
    return 0;
  }

  return toBig(100)
    .minus(getOverprovisionedPercent(requested, provisioned))
    .toNumber();
};

export const toCalcMemoryUnit = (args?: {
  amount?: string | number | null;
  // We pass string here, as BE types h
  unit?: string;
}): number => {
  if (args?.amount == null) {
    return 0;
  }
  const { amount, unit } = args;
  const numericAmount = toNumOrZero(amount);
  if (!isMemoryUnit(unit)) {
    console.error(!unit ? 'No memory unit' : `Invalid unit ${unit}`);
    captureMessage(
      `In PLG cluster dashboard, we're doing assumption, that memory is in MiB, because no valid memory unit is passed. Given unit is '${unit}'`
    );
    return numericAmount;
  }
  return convertUnit(numericAmount, unit, calculationsMemoryUnit);
};

export const toDisplayMemoryUnit = (amount?: number): Memory => {
  return normalizeMemoryUnit({
    amount: toNumOrZero(amount),
    unit: calculationsMemoryUnit,
  });
};

export { getSummaryTendency };

export const getSummaryTendencyColor = (value?: number): string => {
  const colorMap = {
    up: 'black',
    down: 'green.500',
    stable: 'grey.400',
  };

  return colorMap[getSummaryTendency(value)];
};
